import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Wallet, ArrowUp, ArrowDown, AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * AdminWalletsTab — iter-230bv
 *
 * Embedded inside Admin Payouts. Lists every vendor wallet with a
 * negative-balance filter, a ledger drawer per vendor, and a manual
 * adjustment modal (credit or debit) with audit-trail enforcement.
 */
export default function AdminWalletsTab() {
  const { apiCall } = useAuth();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [negOnly, setNegOnly] = useState(false);
  const [unexpectedOnly, setUnexpectedOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  // Drawer/dialog state
  const [drawerVendor, setDrawerVendor] = useState(null);
  const [drawerLedger, setDrawerLedger] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [adjustFor, setAdjustFor] = useState(null);
  const [adjustDir, setAdjustDir] = useState('credit');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNote, setAdjustNote] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (negOnly) params.set('has_negative_balance', 'true');
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '500');
      const res = await apiCall(`/api/admin/vendor-wallets?${params.toString()}`);
      setWallets(res?.wallets || []);
    } catch (e) {
      toast.error('Failed to load wallets');
    } finally {
      setLoading(false);
    }
  }, [apiCall, negOnly, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = wallets.filter((w) => {
    if (unexpectedOnly && !w.unexpected_balance) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (w.vendor_name || '').toLowerCase().includes(q)
      || (w.vendor_email || '').toLowerCase().includes(q)
      || (w.vendor_id || '').toLowerCase().includes(q)
    );
  });

  const unexpectedCount = wallets.filter((w) => w.unexpected_balance).length;

  const totals = wallets.reduce(
    (acc, w) => {
      acc.count += 1;
      acc.balance += Number(w.balance || 0);
      acc.lifetime_credits += Number(w.lifetime_credits || 0);
      acc.lifetime_debits += Number(w.lifetime_debits || 0);
      if ((w.balance || 0) < 0) acc.neg_count += 1;
      return acc;
    },
    { count: 0, balance: 0, lifetime_credits: 0, lifetime_debits: 0, neg_count: 0 }
  );

  async function openDrawer(vendor) {
    setDrawerVendor(vendor);
    setDrawerLedger([]);
    setDrawerLoading(true);
    try {
      const res = await apiCall(`/api/admin/vendor-wallets/${vendor.vendor_id}/ledger?limit=200`);
      setDrawerLedger(res?.ledger || []);
    } catch (e) {
      toast.error('Failed to load ledger');
    } finally {
      setDrawerLoading(false);
    }
  }

  async function submitAdjustment() {
    if (!adjustFor) return;
    const amt = parseFloat(adjustAmount);
    if (!amt || amt <= 0) {
      toast.error('Amount must be > 0');
      return;
    }
    if (adjustNote.trim().length < 3) {
      toast.error('Note required (≥ 3 chars)');
      return;
    }
    setAdjustSaving(true);
    try {
      await apiCall(
        `/api/admin/vendor-wallets/${adjustFor.vendor_id}/adjust`,
        {
          method: 'POST',
          body: JSON.stringify({
            direction: adjustDir,
            amount: amt,
            note: adjustNote.trim(),
          }),
        }
      );
      toast.success(`${adjustDir === 'credit' ? 'Credited' : 'Debited'} ₹${amt.toFixed(2)}`);
      setAdjustFor(null);
      setAdjustAmount('');
      setAdjustNote('');
      load();
    } catch (e) {
      toast.error('Adjustment failed');
    } finally {
      setAdjustSaving(false);
    }
  }

  const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div data-testid="admin-wallets-tab" className="space-y-6">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500">Wallets</div>
            <div className="text-2xl font-bold mt-1" data-testid="wallets-total-count">{totals.count}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500">Total balance</div>
            <div
              className={`text-2xl font-bold mt-1 ${totals.balance < 0 ? 'text-rose-600' : 'text-emerald-600'}`}
              data-testid="wallets-total-balance"
            >
              {totals.balance < 0 ? '−' : ''}
              {fmt(Math.abs(totals.balance))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 flex items-center gap-1">
              <ArrowUp className="w-3 h-3 text-emerald-600" />
              Lifetime credits
            </div>
            <div className="text-2xl font-bold mt-1 text-zinc-900" data-testid="wallets-lifetime-credits">
              {fmt(totals.lifetime_credits)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500 flex items-center gap-1">
              <ArrowDown className="w-3 h-3 text-rose-600" />
              Negative balance
            </div>
            <div className="text-2xl font-bold mt-1 text-rose-600" data-testid="wallets-neg-count">
              {totals.neg_count}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Wallet className="w-4 h-4 text-emerald-600" />
            Shipping Pool Wallets
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <Input
              data-testid="wallets-search"
              placeholder="Search by name / email / id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="md:max-w-xs"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="wallets-status-filter" className="md:max-w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="hibernated">Hibernated</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="wallets-neg-only"
                id="neg-only"
                checked={negOnly}
                onCheckedChange={setNegOnly}
              />
              <Label htmlFor="neg-only" className="text-sm cursor-pointer">
                Negative balance only
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                data-testid="wallets-unexpected-only"
                id="unexpected-only"
                checked={unexpectedOnly}
                onCheckedChange={setUnexpectedOnly}
              />
              <Label htmlFor="unexpected-only" className="text-sm cursor-pointer flex items-center gap-1">
                Self-managed with balance only
                {unexpectedCount > 0 && (
                  <Badge variant="outline" className="text-amber-700 bg-amber-50 border-amber-200 ml-1" data-testid="wallets-unexpected-count">
                    {unexpectedCount}
                  </Badge>
                )}
              </Label>
            </div>
            <Button
              data-testid="wallets-refresh"
              type="button"
              variant="outline"
              size="sm"
              onClick={load}
              className="ml-auto"
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-sm text-zinc-500 py-8 text-center">Loading wallets…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-zinc-500 py-8 text-center">No wallets match the filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
                  <tr>
                    <th className="text-left px-3 py-2">Vendor</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Balance</th>
                    <th className="text-right px-3 py-2">Lifetime ↑</th>
                    <th className="text-right px-3 py-2">Lifetime ↓</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((w) => {
                    const neg = (w.balance || 0) < 0;
                    return (
                      <tr
                        key={w.id}
                        data-testid="admin-wallet-row"
                        className="border-t border-zinc-100 hover:bg-zinc-50/50"
                      >
                        <td className="px-3 py-2">
                          <div className="font-medium text-zinc-900">{w.vendor_name}</div>
                          <div className="text-xs text-zinc-500">{w.vendor_email}</div>
                          {w.unexpected_balance && (
                            <Badge
                              variant="outline"
                              data-testid="wallet-unexpected-badge"
                              className="text-amber-700 bg-amber-50 border-amber-200 mt-1"
                              title="This vendor is self-managed shipping — a non-zero shipping pool balance shouldn't exist. Verify and correct via Adjust."
                            >
                              <AlertTriangle className="w-3 h-3 mr-1" /> Self-managed, unexpected balance
                            </Badge>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            variant="outline"
                            className={
                              w.status === 'locked' ? 'text-amber-700 bg-amber-50 border-amber-200'
                              : w.status === 'hibernated' ? 'text-zinc-600 bg-zinc-100 border-zinc-200'
                              : 'text-emerald-700 bg-emerald-50 border-emerald-200'
                            }
                          >
                            {w.status}
                          </Badge>
                        </td>
                        <td
                          data-testid="admin-wallet-balance-cell"
                          className={`px-3 py-2 text-right font-semibold tabular-nums ${neg ? 'text-rose-600' : 'text-zinc-900'}`}
                        >
                          {neg ? '−' : ''}
                          {fmt(Math.abs(w.balance))}
                          {neg ? <AlertTriangle className="inline w-3 h-3 ml-1 text-rose-500" /> : null}
                        </td>
                        <td className="px-3 py-2 text-right text-emerald-700 tabular-nums">
                          {fmt(w.lifetime_credits)}
                        </td>
                        <td className="px-3 py-2 text-right text-rose-700 tabular-nums">
                          {fmt(w.lifetime_debits)}
                        </td>
                        <td className="px-3 py-2 text-right space-x-2">
                          <Button
                            data-testid="admin-wallet-view-ledger"
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => openDrawer(w)}
                          >
                            Ledger
                          </Button>
                          <Button
                            data-testid="admin-wallet-adjust"
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => { setAdjustFor(w); setAdjustDir('credit'); }}
                          >
                            Adjust
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ledger drawer (Dialog used as drawer) */}
      <Dialog open={!!drawerVendor} onOpenChange={(o) => !o && setDrawerVendor(null)}>
        <DialogContent
          data-testid="admin-wallet-ledger-drawer"
          className="max-w-2xl max-h-[85vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-emerald-600" />
              {drawerVendor?.vendor_name} — Wallet Ledger
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-zinc-500">
            Current balance:&nbsp;
            <span className={(drawerVendor?.balance || 0) < 0 ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold'}>
              {(drawerVendor?.balance || 0) < 0 ? '−' : ''}
              {fmt(Math.abs(drawerVendor?.balance || 0))}
            </span>
            &nbsp;•&nbsp;Status: <span className="font-semibold capitalize">{drawerVendor?.status}</span>
          </div>
          {drawerLoading ? (
            <div className="text-sm text-zinc-500 py-6 text-center">Loading…</div>
          ) : drawerLedger.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No activity.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {drawerLedger.map((row) => {
                const isCredit = ['credit', 'payout_recovery', 'activate'].includes(row.txn_type);
                return (
                  <li key={row.id} data-testid="admin-wallet-ledger-row" className="py-2 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-800">{row.note || row.txn_type}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        {(row.created_at || '').slice(0, 19).replace('T', ' ')} • {row.txn_type}
                        {row.actor ? ` • by ${row.actor}` : ''}
                      </div>
                    </div>
                    <div className={`text-right text-sm font-semibold tabular-nums whitespace-nowrap ${isCredit ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {isCredit ? '+' : '−'}
                      {fmt(row.amount)}
                      <div className="text-[10px] font-normal text-zinc-400">bal {fmt(row.balance_after)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {/* Adjustment modal */}
      <Dialog open={!!adjustFor} onOpenChange={(o) => !o && setAdjustFor(null)}>
        <DialogContent data-testid="admin-wallet-adjust-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adjust {adjustFor?.vendor_name}&apos;s wallet</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Direction</Label>
              <Select value={adjustDir} onValueChange={setAdjustDir}>
                <SelectTrigger data-testid="admin-wallet-adjust-direction" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credit">Credit (add funds)</SelectItem>
                  <SelectItem value="debit">Debit (deduct funds)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                data-testid="admin-wallet-adjust-amount"
                type="number"
                min="0"
                step="0.01"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="0.00"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Audit note (required)</Label>
              <Input
                data-testid="admin-wallet-adjust-note"
                value={adjustNote}
                onChange={(e) => setAdjustNote(e.target.value)}
                placeholder="e.g. Goodwill credit for lost-package refund — ticket #1234"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="admin-wallet-adjust-cancel"
              type="button"
              variant="outline"
              onClick={() => setAdjustFor(null)}
              disabled={adjustSaving}
            >
              Cancel
            </Button>
            <Button
              data-testid="admin-wallet-adjust-submit"
              type="button"
              onClick={submitAdjustment}
              disabled={adjustSaving}
            >
              {adjustSaving ? 'Saving…' : `${adjustDir === 'credit' ? 'Credit' : 'Debit'} wallet`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
